import { parseExpenseDate } from './expenseUtils';
import { ORDER_STATUS } from '../constants';

const roundMoney = (value) => Number((parseFloat(value) || 0).toFixed(2));

export const getWorkshopSewingRate = (productType, adminSettings = {}) => {
  if (productType === 'tul') {
    return Number(adminSettings.tulAtolyeDikimCost) || 0;
  }
  if (productType === 'kanat') {
    return Number(adminSettings.kanatAtolyeDikimCost) || 0;
  }
  return 0;
};

export const isRetailCustomer = (customer) => {
  const type = customer?.type;
  return type !== 'wholesale' && type !== 'bayi';
};

export const buildRetailCustomerIdSet = (customers = []) =>
  new Set(customers.filter(isRetailCustomer).map((c) => c.id));

export const buildCustomerOrderStatusMap = (customers = []) => {
  const map = new Map();
  customers.forEach((customer) => {
    map.set(customer.id, customer.orderStatus);
  });
  return map;
};

export const isRetailTulKanatOrder = (order, retailCustomerIds) => {
  if (!order || (order.productType !== 'tul' && order.productType !== 'kanat')) return false;
  if (!retailCustomerIds?.has(order.customerId)) return false;
  const usedM2 = parseFloat(order.usedM2) || 0;
  const height = parseFloat(order.height) || 0;
  return usedM2 > 0 && height > 0;
};

export const WORKSHOP_ELIGIBLE_ORDER_STATUSES = [
  ORDER_STATUS.ATOLYE,
  ORDER_STATUS.MONTAJ,
  ORDER_STATUS.DELIVERED
];

export const isWorkshopEligibleOrder = (order, retailCustomerIds, customerOrderStatusMap) => {
  if (!isRetailTulKanatOrder(order, retailCustomerIds)) return false;
  const status = customerOrderStatusMap?.get(order.customerId);
  return WORKSHOP_ELIGIBLE_ORDER_STATUSES.includes(status);
};

export const calculateWorkshopSewingCost = (order, adminSettings = {}) => {
  if (!order || (order.productType !== 'tul' && order.productType !== 'kanat')) return 0;
  const usedM2 = parseFloat(order.usedM2) || 0;
  if (usedM2 <= 0) return 0;
  const qty = parseInt(order.quantity, 10) || 1;
  const storedRate = parseFloat(order.workshopSewingRate);
  const rate = Number.isFinite(storedRate) && storedRate > 0
    ? storedRate
    : getWorkshopSewingRate(order.productType, adminSettings);
  return roundMoney(usedM2 * rate * qty);
};

export const resolveOrderWorkshopSewingCost = (order, adminSettings = {}) => {
  const calculated = calculateWorkshopSewingCost(order, adminSettings);
  const stored = parseFloat(order?.workshopSewingCost);
  if (Number.isFinite(stored) && stored > 0) return roundMoney(stored);
  return calculated;
};

const parseWorkshopOrderDate = (order, customerOrderDateMap) => {
  const raw = order?.orderDate || customerOrderDateMap?.get(order.customerId);
  if (!raw) return null;
  return parseExpenseDate(raw);
};

export const getCalendarWeekRange = (referenceDate = new Date()) => {
  const ref = new Date(referenceDate.getFullYear(), referenceDate.getMonth(), referenceDate.getDate());
  const day = ref.getDay();
  const daysFromMonday = day === 0 ? 6 : day - 1;
  const startOfWeek = new Date(ref);
  startOfWeek.setDate(ref.getDate() - daysFromMonday);
  startOfWeek.setHours(0, 0, 0, 0);
  const endOfWeek = new Date(startOfWeek);
  endOfWeek.setDate(startOfWeek.getDate() + 7);
  return { startOfWeek, endOfWeek };
};

export const buildCustomerOrderDateMap = (customers = []) => {
  const map = new Map();
  customers.forEach((customer) => {
    if (customer.orderDate) {
      map.set(customer.id, customer.orderDate);
    }
  });
  return map;
};

export const aggregateWorkshopExpensesFromOrders = (
  orders = [],
  customers = [],
  adminSettings = {},
  referenceDate = new Date()
) => {
  const retailCustomerIds = buildRetailCustomerIdSet(customers);
  const customerOrderStatusMap = buildCustomerOrderStatusMap(customers);
  const customerOrderDateMap = buildCustomerOrderDateMap(customers);
  const now = referenceDate;
  const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfTomorrow = new Date(startOfDay);
  startOfTomorrow.setDate(startOfTomorrow.getDate() + 1);
  const { startOfWeek, endOfWeek } = getCalendarWeekRange(now);
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const startOfNextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  const startOfYear = new Date(now.getFullYear(), 0, 1);
  const currentYear = now.getFullYear();

  const monthlyCounts = Array(12).fill(0);
  let totalAllTime = 0;
  let totalToday = 0;
  let totalWeek = 0;
  let totalMonth = 0;
  let totalYear = 0;

  orders.forEach((order) => {
    if (!isWorkshopEligibleOrder(order, retailCustomerIds, customerOrderStatusMap)) return;
    const amount = resolveOrderWorkshopSewingCost(order, adminSettings);
    if (amount <= 0) return;

    const orderDate = parseWorkshopOrderDate(order, customerOrderDateMap);
    totalAllTime += amount;

    if (orderDate) {
      if (orderDate >= startOfDay && orderDate < startOfTomorrow) totalToday += amount;
      if (orderDate >= startOfWeek && orderDate < endOfWeek) totalWeek += amount;
      if (orderDate >= startOfMonth && orderDate < startOfNextMonth) totalMonth += amount;
      if (orderDate >= startOfYear) totalYear += amount;
      if (orderDate.getFullYear() === currentYear) {
        monthlyCounts[orderDate.getMonth()] += amount;
      }
    }
  });

  return {
    totalAllTime: roundMoney(totalAllTime),
    totalToday: roundMoney(totalToday),
    totalWeek: roundMoney(totalWeek),
    totalMonth: roundMoney(totalMonth),
    totalYear: roundMoney(totalYear),
    monthlyCounts: monthlyCounts.map((v) => roundMoney(v))
  };
};

export const aggregateWorkshopPayments = (payments = []) => {
  let totalPaid = 0;
  payments.forEach((payment) => {
    totalPaid += parseFloat(payment.amount) || 0;
  });
  return roundMoney(totalPaid);
};

export const mergeExpenseAggregates = (manualAgg, workshopAgg) => ({
  totalAllTime: roundMoney(manualAgg.totalAllTime + workshopAgg.totalAllTime),
  totalToday: roundMoney(manualAgg.totalToday + workshopAgg.totalToday),
  totalWeek: roundMoney(manualAgg.totalWeek + workshopAgg.totalWeek),
  totalMonth: roundMoney(manualAgg.totalMonth + workshopAgg.totalMonth),
  totalYear: roundMoney(manualAgg.totalYear + workshopAgg.totalYear),
  monthlyCounts: manualAgg.monthlyCounts.map((value, index) =>
    roundMoney(value + (workshopAgg.monthlyCounts[index] || 0))
  ),
  manualTotalAllTime: manualAgg.totalAllTime,
  workshopTotalAllTime: workshopAgg.totalAllTime
});

export const buildWorkshopOrderRows = (orders = [], customers = [], adminSettings = {}) => {
  const retailCustomerIds = buildRetailCustomerIdSet(customers);
  const customerOrderStatusMap = buildCustomerOrderStatusMap(customers);
  const customerOrderDateMap = buildCustomerOrderDateMap(customers);
  const customerNameMap = new Map(customers.map((c) => [c.id, c.name || '—']));

  const rows = [];
  orders.forEach((order) => {
    if (!isWorkshopEligibleOrder(order, retailCustomerIds, customerOrderStatusMap)) return;
    const amount = resolveOrderWorkshopSewingCost(order, adminSettings);
    if (amount <= 0) return;

    rows.push({
      id: order.id,
      customerName: customerNameMap.get(order.customerId) || '—',
      productName: order.productName || '—',
      productType: order.productType,
      usedM2: parseFloat(order.usedM2) || 0,
      quantity: parseInt(order.quantity, 10) || 1,
      amount,
      orderDate: parseWorkshopOrderDate(order, customerOrderDateMap)
    });
  });

  return rows.sort((a, b) => {
    const dateA = a.orderDate?.getTime() || 0;
    const dateB = b.orderDate?.getTime() || 0;
    if (dateB !== dateA) return dateB - dateA;
    return String(b.id).localeCompare(String(a.id));
  });
};
